import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { EditScheduledMatchDialog, type ScheduledMatchDialogValue } from "./EditScheduledMatchDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { downloadCalendarEvent, type CalendarEventData } from "@/lib/icalendar";
import { hasNativeCalendar, nativeAddToCalendar } from "@/lib/nativeBridge";
import { 
  CalendarDays, 
  Clock, 
  MapPin, 
  Trash2, 
  Plus, 
  Minus, 
  Check, 
  X, 
  Loader2,
  AlertCircle,
  Trophy,
  ChevronRight,
  Calendar,
  Pencil,
  Lock
} from "lucide-react";
import { LockedScheduledResultDialog } from "./LockedScheduledResultDialog";
import { CompletedScheduledMatchDetailsDialog } from "./CompletedScheduledMatchDetailsDialog";
import { format, parseISO, isAfter, isBefore, isToday } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface PlayerInfo {
  id: string;
  name: string;
  image: string | null;
}

interface ScheduledMatchesApiResponse {
  matches: ScheduledMatch[];
  serverTimezone: string;
}

interface ScheduledMatch {
  id: string;
  organizerId: string;
  leagueId: string | null;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  court: string | null;
  team1Player1Id: string;
  team1Player2Id: string | null;
  team2Player1Id: string;
  team2Player2Id: string | null;
  status: "scheduled" | "score_pending" | "score_submitted" | "completed" | "cancelled";
  winner: 1 | 2 | null;
  score: { sets: Array<{ team1: number; team2: number }> } | null;
  scoreSubmittedById: string | null;
  scoreSubmitterTeam: 1 | 2 | null;
  notes: string | null;
  team1Player1: PlayerInfo;
  team1Player2: PlayerInfo | null;
  team2Player1: PlayerInfo;
  team2Player2: PlayerInfo | null;
  updatedAt?: string | null;
  currentUserLeagueRole?: "owner" | "admin" | "member" | null;
  homeClubId?: string | null;
  homeClubName?: string | null;
}

interface ScheduledMatchesListProps {
  userId?: string;
  limit?: number;
  showViewAll?: boolean;
  onViewAll?: () => void;
  compact?: boolean;
  // When set, auto-opens the "Agregar Score" dialog (or the locked
  // dialog if the match start time hasn't been reached) for the match
  // with this id, then calls `onResultDeepLinkConsumed` so the parent
  // can clear the request.
  resultDeepLinkMatchId?: string | null;
  onResultDeepLinkConsumed?: () => void;
}

function buildMatchCalendarEvent(match: ScheduledMatch): CalendarEventData {
  const [hours, minutes] = match.scheduledTime.split(':').map(Number);
  const [year, month, day] = match.scheduledDate.split('-').map(Number);
  const start = new Date(year, month - 1, day, hours, minutes);
  const end = new Date(start.getTime() + 90 * 60000);
  return {
    title: `Partido de Pádel - ${match.team1Player1.name.split(' ')[0]} vs ${match.team2Player1.name.split(' ')[0]}`,
    start,
    end,
    location: match.location,
    description: `${match.team1Player1.name}${match.team1Player2 ? ` y ${match.team1Player2.name}` : ''} vs ${match.team2Player1.name}${match.team2Player2 ? ` y ${match.team2Player2.name}` : ''}`,
  };
}

export function ScheduledMatchesList({
  userId,
  limit,
  showViewAll,
  onViewAll,
  compact = false,
  resultDeepLinkMatchId,
  onResultDeepLinkConsumed,
}: ScheduledMatchesListProps) {
  const { toast } = useToast();

  const handleAddToCalendar = async (match: ScheduledMatch) => {
    const event = buildMatchCalendarEvent(match);
    if (hasNativeCalendar()) {
      const r = await nativeAddToCalendar({
        title: event.title,
        description: event.description,
        startISO: event.start.toISOString(),
        endISO: event.end.toISOString(),
        location: event.location,
      });
      if (r?.ok) {
        toast({ title: "Añadido al calendario" });
        return;
      }
      if (r?.cancelled) return;
      if (r?.permission === "denied") {
        toast({
          title: "Permiso de calendario denegado",
          description: "Activa el acceso al calendario en los ajustes de la app para añadir el partido.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "No se pudo añadir al calendario",
        description: r?.error || "Inténtalo de nuevo más tarde.",
        variant: "destructive",
      });
      return;
    }
    downloadCalendarEvent(event);
  };
  const [selectedMatch, setSelectedMatch] = useState<ScheduledMatch | null>(null);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [lockedMatch, setLockedMatch] = useState<ScheduledMatch | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [matchToCancel, setMatchToCancel] = useState<string | null>(null);
  // Standalone (no-league) match the current user is editing. The dialog
  // closes by setting this back to null. League matches still get edited
  // from `LeagueDetailView`.
  const [editTarget, setEditTarget] = useState<ScheduledMatch | null>(null);
  // Standalone completed match the user opened to see the rich detail. The
  // detail dialog fetches `/api/scheduled-matches/:id` to pull the
  // reporter/approver names and timestamps that the list endpoint omits.
  const [detailMatchId, setDetailMatchId] = useState<string | null>(null);

  const [sets, setSets] = useState<Array<{ team1: number; team2: number }>>([
    { team1: 0, team2: 0 },
  ]);

  const { data: matches = [], isLoading } = useQuery<
    ScheduledMatchesApiResponse,
    Error,
    ScheduledMatch[]
  >({
    queryKey: ["/api/scheduled-matches"],
    select: (data) => data.matches,
  });

  const { data: pendingScores = [] } = useQuery<
    ScheduledMatchesApiResponse,
    Error,
    ScheduledMatch[]
  >({
    queryKey: ["/api/scheduled-matches/pending-scores"],
    select: (data) => data.matches,
  });

  // Open the score dialog (or scroll to the card) automatically when a
  // notification deep link (e.g. the 75-minute "registra el resultado"
  // reminder) lands the user here via `?action=register-result&match=...`.
  // Waits until the matches list resolves and only fires once per id.
  const lastConsumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!resultDeepLinkMatchId) return;
    if (lastConsumedRef.current === resultDeepLinkMatchId) return;
    if (isLoading) return;

    const target = matches.find((m) => m.id === resultDeepLinkMatchId);
    if (!target) {
      // Match isn't in this user's scheduled-matches list. Nothing to do —
      // the parent's focusMatchCard retry loop (started on mount/message)
      // already handles the scroll if the card exists elsewhere on the page.
      return;
    }

    lastConsumedRef.current = resultDeepLinkMatchId;

    if (target.status === "scheduled") {
      const matchDateTime = parseISO(
        `${target.scheduledDate}T${target.scheduledTime}`,
      );
      if (isBefore(matchDateTime, new Date())) {
        // Match time has passed — open the score-entry dialog directly.
        setSelectedMatch(target);
        setShowScoreDialog(true);
      } else {
        // Match hasn't started yet — show the locked-result dialog.
        setLockedMatch(target);
      }
    } else {
      // For any other status (e.g. score_pending, completed) the match card
      // is already in the DOM now that data has loaded. Scroll straight to it
      // so the user doesn't have to hunt for it. The parent's focusMatchCard
      // retry loop may have already exhausted its attempts by this point, so
      // we do a direct lookup here as the reliable fallback.
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `[data-testid="scheduled-match-${resultDeepLinkMatchId}"]`,
        ) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-primary", "ring-offset-2");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-primary", "ring-offset-2");
          }, 2500);
        }
      });
    }

    onResultDeepLinkConsumed?.();
  }, [
    resultDeepLinkMatchId,
    matches,
    isLoading,
    onResultDeepLinkConsumed,
  ]);

  // Friend list is needed to populate the player picker when editing a
  // standalone match. Fetch it lazily, only after the user opens the edit
  // dialog on a no-league match (matches the API's allowed-candidate rules
  // for the PATCH /api/scheduled-matches/:id no-league branch).
  const { data: friends = [] } = useQuery<
    Array<{ id: string; name: string }>
  >({
    queryKey: ["/api/friends"],
    enabled: !!editTarget && !editTarget.leagueId,
  });

  // Members offered in the picker = current 4 players (so they aren't lost
  // even if they aren't friends anymore) merged with the user's friends.
  const editDialogMembers = useMemo(() => {
    if (!editTarget) return [];
    const map = new Map<string, { userId: string; userName: string }>();
    const addPlayer = (id: string | null, name: string | null | undefined) => {
      if (!id || map.has(id)) return;
      map.set(id, { userId: id, userName: name ?? "Jugador" });
    };
    addPlayer(editTarget.team1Player1Id, editTarget.team1Player1?.name);
    addPlayer(editTarget.team1Player2Id, editTarget.team1Player2?.name);
    addPlayer(editTarget.team2Player1Id, editTarget.team2Player1?.name);
    addPlayer(editTarget.team2Player2Id, editTarget.team2Player2?.name);
    for (const f of friends) {
      addPlayer(f.id, f.name);
    }
    return Array.from(map.values());
  }, [editTarget, friends]);

  const editDialogValue: ScheduledMatchDialogValue | null = editTarget
    ? {
        id: editTarget.id,
        leagueId: editTarget.leagueId,
        scheduledDate: editTarget.scheduledDate,
        scheduledTime: editTarget.scheduledTime,
        location: editTarget.location,
        court: editTarget.court,
        notes: editTarget.notes,
        team1Player1Id: editTarget.team1Player1Id,
        team1Player2Id: editTarget.team1Player2Id,
        team2Player1Id: editTarget.team2Player1Id,
        team2Player2Id: editTarget.team2Player2Id,
        homeClubId: (editTarget as any).homeClubId ?? null,
        homeClubName:
          (editTarget as any).homeClub?.name ??
          (editTarget as any).homeClubName ??
          null,
      }
    : null;

  const submitScoreMutation = useMutation({
    mutationFn: async ({ matchId, score, winner }: { matchId: string; score: any; winner: 1 | 2 }) => {
      return apiRequest("POST", `/api/scheduled-matches/${matchId}/score`, { score, winner });
    },
    onSuccess: () => {
      toast({
        title: "Score registrado",
        description: "El resultado ha sido enviado para aprobación.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
      setShowScoreDialog(false);
      setSelectedMatch(null);
      setSets([{ team1: 0, team2: 0 }]);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo registrar el score",
        variant: "destructive",
      });
    },
  });

  const approveScoreMutation = useMutation({
    mutationFn: async (matchId: string) => {
      return apiRequest("POST", `/api/scheduled-matches/${matchId}/approve`);
    },
    onSuccess: () => {
      toast({
        title: "Score aprobado",
        description: "El resultado ha sido confirmado.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches/pending-scores"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo aprobar el score",
        variant: "destructive",
      });
    },
  });

  const rejectScoreMutation = useMutation({
    mutationFn: async (matchId: string) => {
      return apiRequest("POST", `/api/scheduled-matches/${matchId}/reject`);
    },
    onSuccess: () => {
      toast({
        title: "Score rechazado",
        description: "El resultado ha sido rechazado. Alguien puede enviar uno nuevo.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches/pending-scores"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo rechazar el score",
        variant: "destructive",
      });
    },
  });

  const cancelMatchMutation = useMutation({
    mutationFn: async (matchId: string) => {
      return apiRequest("DELETE", `/api/scheduled-matches/${matchId}`);
    },
    onSuccess: () => {
      toast({
        title: "Partido cancelado",
        description: "El partido ha sido cancelado.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
      setShowCancelDialog(false);
      setMatchToCancel(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo cancelar el partido",
        variant: "destructive",
      });
    },
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Hoy";
    return format(date, "EEE d MMM", { locale: es });
  };

  const canAddScore = (match: ScheduledMatch) => {
    if (match.status !== "scheduled") return false;
    const matchDateTime = parseISO(`${match.scheduledDate}T${match.scheduledTime}`);
    return isBefore(matchDateTime, new Date());
  };

  const canApproveScore = (match: ScheduledMatch) => {
    if (match.status !== "score_submitted") return false;
    const isTeam1 = match.team1Player1Id === userId || match.team1Player2Id === userId;
    const isTeam2 = match.team2Player1Id === userId || match.team2Player2Id === userId;
    if (match.scoreSubmitterTeam === 1) return isTeam2;
    if (match.scoreSubmitterTeam === 2) return isTeam1;
    return false;
  };

  const addSet = () => {
    if (sets.length < 3) {
      setSets([...sets, { team1: 0, team2: 0 }]);
    }
  };

  const removeSet = () => {
    if (sets.length > 1) {
      setSets(sets.slice(0, -1));
    }
  };

  const updateSetScore = (index: number, team: "team1" | "team2", value: number) => {
    const newSets = [...sets];
    newSets[index][team] = Math.max(0, Math.min(7, value));
    setSets(newSets);
  };

  const calculateWinner = (): 1 | 2 | null => {
    let team1Sets = 0;
    let team2Sets = 0;
    sets.forEach((set) => {
      if (set.team1 > set.team2) team1Sets++;
      else if (set.team2 > set.team1) team2Sets++;
    });
    if (team1Sets > team2Sets) return 1;
    if (team2Sets > team1Sets) return 2;
    return null;
  };

  const handleSubmitScore = () => {
    const winner = calculateWinner();
    if (!winner || !selectedMatch) {
      toast({
        title: "Score incompleto",
        description: "Debe haber un ganador claro en los sets.",
        variant: "destructive",
      });
      return;
    }
    submitScoreMutation.mutate({
      matchId: selectedMatch.id,
      score: { sets },
      winner,
    });
  };

  const upcomingMatches = matches.filter(
    (m) =>
      (m.status === "scheduled" || m.status === "score_pending") &&
      isAfter(parseISO(m.scheduledDate), new Date())
  );

  const playedMatches = matches.filter(
    (m) =>
      (m.status === "scheduled" || m.status === "score_pending") &&
      !isAfter(parseISO(m.scheduledDate), new Date())
  );

  const pendingApprovalMatches = matches.filter((m) => m.status === "score_submitted");
  // Only show recently completed scheduled matches (last 14 days) to mirror
  // the league screen + mobile Partidos tab behavior from task #147. The
  // recency check uses `updatedAt` (when the result was last touched /
  // approved) so a match scheduled long ago but approved recently still
  // shows up. Falls back to `scheduledDate` when `updatedAt` is missing.
  const recentCompletedCutoffMs = Date.now() - 14 * 24 * 60 * 60 * 1000;
  const recencyOf = (m: ScheduledMatch) => {
    const updated = m.updatedAt ? Date.parse(m.updatedAt) : Number.NaN;
    return Number.isFinite(updated) ? updated : parseISO(m.scheduledDate).getTime();
  };
  const completedMatches = matches
    .filter((m) => {
      if (m.status !== "completed") return false;
      const recencyMs = recencyOf(m);
      return Number.isFinite(recencyMs) && recencyMs >= recentCompletedCutoffMs;
    })
    // Most recently approved first, matching the mobile Partidos screen.
    .sort((a, b) => recencyOf(b) - recencyOf(a));
  const cancelledMatches = matches.filter((m) => m.status === "cancelled");

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Compact mode for Home screen - just show upcoming matches
  if (limit !== undefined || compact) {
    const displayMatches = upcomingMatches.slice(0, limit || 5);
    
    if (displayMatches.length === 0) {
      return (
        <div className="h-32 flex flex-col items-center justify-center text-center">
          <CalendarDays className="h-10 w-10 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">
            No tienes partidos agendados
          </p>
          <p className="text-center text-xs text-muted-foreground mt-3">
            Agenda un partido con tus amigos
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {displayMatches.map((match) => (
          <div
            key={match.id}
            className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover-elevate"
            data-testid={`upcoming-match-${match.id}`}
          >
            <div className="p-2 rounded-full bg-primary/10">
              <CalendarDays className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm">
                  {match.team1Player1.name.split(' ')[0]}
                  {match.team1Player2 && ` / ${match.team1Player2.name.split(' ')[0]}`}
                  {" vs "}
                  {match.team2Player1.name.split(' ')[0]}
                  {match.team2Player2 && ` / ${match.team2Player2.name.split(' ')[0]}`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span>{formatDate(match.scheduledDate)}</span>
                <span>•</span>
                <span>{match.scheduledTime}</span>
                <span>•</span>
                <MapPin className="h-3 w-3" />
                <span className="truncate">
                  {match.homeClubName ? `${match.homeClubName} · ` : ""}
                  {match.location}
                </span>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={() => { void handleAddToCalendar(match); }}
              title="Agregar al calendario"
              data-testid={`button-add-calendar-${match.id}`}
            >
              <Calendar className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        {showViewAll && upcomingMatches.length > (limit || 5) && onViewAll && (
          <Button 
            variant="ghost" 
            className="w-full text-sm"
            onClick={onViewAll}
            data-testid="button-view-all-upcoming"
          >
            Ver todos los partidos
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>
    );
  }

  const isParticipant = (match: ScheduledMatch) => {
    if (!userId) return false;
    return (
      match.team1Player1Id === userId ||
      match.team1Player2Id === userId ||
      match.team2Player1Id === userId ||
      match.team2Player2Id === userId
    );
  };

  const MatchCard = ({ match, showActions = true }: { match: ScheduledMatch; showActions?: boolean }) => {
    const isOrganizer = match.organizerId === userId;
    const isLeagueAdmin =
      !!match.leagueId &&
      (match.currentUserLeagueRole === "owner" ||
        match.currentUserLeagueRole === "admin");
    const canCancel =
      (isOrganizer || isLeagueAdmin) &&
      match.status !== "completed" &&
      match.status !== "cancelled";
    const showAddScore = canAddScore(match);
    const showApprove = canApproveScore(match);
    // Standalone matches can be edited by any of the 4 players. League
    // matches are still edited from `LeagueDetailView` (admin-only flow),
    // so we only surface the edit button here for no-league matches.
    const canEditStandalone =
      match.status === "scheduled" &&
      !match.leagueId &&
      isParticipant(match);
    // Standalone completed matches open the rich detail dialog (final
    // score, set-by-set, who reported, who confirmed). League completed
    // matches stay non-interactive here because they have richer flows
    // from the league screen.
    const canOpenDetails =
      match.status === "completed" && !match.leagueId;

    const handleCardClick = canOpenDetails
      ? () => setDetailMatchId(match.id)
      : undefined;

    return (
      <div
        className={cn(
          "p-3 rounded-lg border bg-card",
          match.status === "cancelled" && "opacity-60",
          canOpenDetails && "hover-elevate cursor-pointer",
        )}
        data-testid={`scheduled-match-${match.id}`}
        role={canOpenDetails ? "button" : undefined}
        tabIndex={canOpenDetails ? 0 : undefined}
        onClick={handleCardClick}
        onKeyDown={
          canOpenDetails
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setDetailMatchId(match.id);
                }
              }
            : undefined
        }
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-sm">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{formatDate(match.scheduledDate)}</span>
            <Clock className="h-3 w-3 text-muted-foreground ml-1" />
            <span className="text-muted-foreground">{match.scheduledTime}</span>
          </div>
          {match.status === "scheduled" && !showAddScore && (
            <Badge variant="secondary" className="text-xs">Próximo</Badge>
          )}
          {showAddScore && (
            <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
              Agregar score
            </Badge>
          )}
          {match.status === "score_submitted" && (
            <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
              Pendiente
            </Badge>
          )}
          {match.status === "completed" && (
            <Badge variant="default" className="text-xs">Completado</Badge>
          )}
          {match.status === "cancelled" && (
            <Badge variant="destructive" className="text-xs">Cancelado</Badge>
          )}
        </div>

        <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3 flex-wrap">
          <MapPin className="h-3 w-3" />
          {match.homeClubName ? (
            <>
              <span className="font-medium text-foreground">
                {match.homeClubName}
              </span>
              <span>·</span>
            </>
          ) : null}
          <span>{match.location}</span>
          {match.court && <span>· {match.court}</span>}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 flex-1">
            <div className="flex -space-x-2">
              <Avatar className="h-7 w-7 border-2 border-background">
                <AvatarImage src={match.team1Player1.image || undefined} />
                <AvatarFallback className="text-[10px]">
                  {getInitials(match.team1Player1.name)}
                </AvatarFallback>
              </Avatar>
              {match.team1Player2 && (
                <Avatar className="h-7 w-7 border-2 border-background">
                  <AvatarImage src={match.team1Player2.image || undefined} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(match.team1Player2.name)}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
            {match.winner === 1 && (
              <Trophy className="h-3 w-3 text-yellow-500 ml-1" />
            )}
          </div>

          {match.score && (
            <div className="flex items-center gap-1 px-2">
              {match.score.sets.map((set, i) => (
                <span key={i} className="text-xs font-mono">
                  {set.team1}-{set.team2}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-1 flex-1 justify-end">
            {match.winner === 2 && (
              <Trophy className="h-3 w-3 text-yellow-500 mr-1" />
            )}
            <div className="flex -space-x-2">
              <Avatar className="h-7 w-7 border-2 border-background">
                <AvatarImage src={match.team2Player1.image || undefined} />
                <AvatarFallback className="text-[10px]">
                  {getInitials(match.team2Player1.name)}
                </AvatarFallback>
              </Avatar>
              {match.team2Player2 && (
                <Avatar className="h-7 w-7 border-2 border-background">
                  <AvatarImage src={match.team2Player2.image || undefined} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(match.team2Player2.name)}
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          </div>
        </div>

        {showActions && (match.status === "scheduled" || match.status === "score_pending" || match.status === "score_submitted") && (
          <div className="flex gap-2 mt-3 pt-3 border-t">
            {match.status === "scheduled" && (
              showAddScore ? (
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1"
                  onClick={() => {
                    setSelectedMatch(match);
                    setShowScoreDialog(true);
                  }}
                  data-testid={`button-add-score-${match.id}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Agregar Score
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 relative text-muted-foreground"
                  onClick={() => setLockedMatch(match)}
                  data-testid={`button-add-score-${match.id}`}
                  aria-label="Agregar Score (bloqueado)"
                >
                  <Trophy className="h-3 w-3 mr-1 opacity-50" />
                  Agregar Score
                  <span
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-zinc-900 text-white flex items-center justify-center border border-background"
                    aria-hidden="true"
                  >
                    <Lock className="h-2.5 w-2.5" />
                  </span>
                </Button>
              )
            )}
            {showApprove && (
              <>
                <Button
                  size="sm"
                  variant="default"
                  className="flex-1"
                  onClick={() => approveScoreMutation.mutate(match.id)}
                  disabled={approveScoreMutation.isPending}
                  data-testid={`button-approve-score-${match.id}`}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Aprobar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => rejectScoreMutation.mutate(match.id)}
                  disabled={rejectScoreMutation.isPending}
                  data-testid={`button-reject-score-${match.id}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </>
            )}
            {match.status === "scheduled" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { void handleAddToCalendar(match); }}
                data-testid={`button-add-calendar-detailed-${match.id}`}
              >
                <Calendar className="h-3 w-3 mr-1" />
                Calendario
              </Button>
            )}
            {canEditStandalone && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditTarget(match)}
                data-testid={`button-edit-scheduled-${match.id}`}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Editar
              </Button>
            )}
            {canCancel && (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive"
                onClick={() => {
                  setMatchToCancel(match.id);
                  setShowCancelDialog(true);
                }}
                data-testid={`button-cancel-match-${match.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {pendingScores.length > 0 && (
        <Card className="border-orange-500/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-orange-600">
              <AlertCircle className="h-4 w-4" />
              Scores Pendientes ({pendingScores.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingScores.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </CardContent>
        </Card>
      )}

      {playedMatches.length > 0 && (
        <Card className="border-blue-500/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-blue-600">
              <Trophy className="h-4 w-4" />
              Agregar Resultados ({playedMatches.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {playedMatches.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </CardContent>
        </Card>
      )}

      {upcomingMatches.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Próximos Partidos ({upcomingMatches.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcomingMatches.map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </CardContent>
        </Card>
      )}

      {/*
        Recently completed scheduled matches used to show here as a
        muted "Completados" card. They were a duplicate: the same
        match is materialized into a real `league_matches` row that
        already appears in "Partidos recientes" with full ELO deltas,
        the trophy icon and rating changes. To match the user's
        expectation that a finished scheduled match looks identical
        to any other recent match, the bucket is intentionally hidden
        on this list as well.
      */}

      {matches.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CalendarDays className="h-12 w-12 text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">No tienes partidos agendados</p>
          <p className="text-xs text-muted-foreground mt-1">
            Agenda un partido desde la sección de amigos
          </p>
        </div>
      )}

      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Registrar Score</DialogTitle>
            <DialogDescription>
              Ingresa el resultado de cada set
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {sets.map((set, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-12">Set {index + 1}</span>
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    type="number"
                    min="0"
                    max="7"
                    value={set.team1}
                    onChange={(e) => updateSetScore(index, "team1", parseInt(e.target.value) || 0)}
                    className="w-14 text-center"
                    data-testid={`input-set-${index}-team1`}
                  />
                  <span className="text-muted-foreground">-</span>
                  <Input
                    type="number"
                    min="0"
                    max="7"
                    value={set.team2}
                    onChange={(e) => updateSetScore(index, "team2", parseInt(e.target.value) || 0)}
                    className="w-14 text-center"
                    data-testid={`input-set-${index}-team2`}
                  />
                </div>
              </div>
            ))}

            <div className="flex gap-2">
              {sets.length < 3 && (
                <Button type="button" size="sm" variant="outline" onClick={addSet}>
                  <Plus className="h-3 w-3 mr-1" />
                  Set
                </Button>
              )}
              {sets.length > 1 && (
                <Button type="button" size="sm" variant="ghost" onClick={removeSet}>
                  <Minus className="h-3 w-3 mr-1" />
                  Set
                </Button>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScoreDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitScore}
              disabled={submitScoreMutation.isPending || !calculateWinner()}
              data-testid="button-confirm-score"
            >
              {submitScoreMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Enviar Score
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar partido</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de que deseas cancelar este partido? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, mantener</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => matchToCancel && cancelMatchMutation.mutate(matchToCancel)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelMatchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditScheduledMatchDialog
        match={editDialogValue}
        members={editDialogMembers}
        requireAllPlayers
        onClose={() => setEditTarget(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/scheduled-matches"] });
          queryClient.invalidateQueries({
            queryKey: ["/api/scheduled-matches/upcoming"],
          });
        }}
      />

      <LockedScheduledResultDialog
        open={!!lockedMatch}
        scheduledDate={lockedMatch?.scheduledDate ?? null}
        scheduledTime={lockedMatch?.scheduledTime ?? null}
        onClose={() => setLockedMatch(null)}
      />

      <CompletedScheduledMatchDetailsDialog
        matchId={detailMatchId}
        onClose={() => setDetailMatchId(null)}
      />
    </div>
  );
}
